import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import ConfirmButton from "@/components/ConfirmButton";

const STATUS_OPTIONS = [
  { value: "draft",     label: "Rascunho",  badge: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "published", label: "Publicado", badge: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  { value: "inactive",  label: "Inativo",   badge: "bg-rose-100 text-rose-900 border-rose-300" },
];

const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
const STATUS_BADGE = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.badge]));

// Mantém os MESMOS limites do backend (routes/downloads.py)
const ALLOWED_EXTS = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "png", "jpg", "jpeg", "zip"];
const ACCEPT_ATTR = ALLOWED_EXTS.map((e) => "." + e).join(",");
const MAX_MB = 25;

const EMPTY_FORM = {
  title: "",
  description: "",
  category: "Geral",
  status: "draft",
  tag: "",
  file: null,
};

function StatusPill({ value }) {
  return (
    <span className={`${STATUS_BADGE[value] || ""} border px-2 py-1 text-[10px] font-bold uppercase tracking-wider`}>
      {STATUS_LABEL[value] || value}
    </span>
  );
}

export default function DownloadsManager() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState({ q: "", category: "", status: "" });
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: list }, { data: cats }] = await Promise.all([
        api.get("/admin/downloads"),
        api.get("/admin/downloads/categories"),
      ]);
      setItems(list);
      setCategories(cats);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return items.filter((it) => {
      if (filter.category && it.category !== filter.category) return false;
      if (filter.status && it.status !== filter.status) return false;
      if (q && !(it.title.toLowerCase().includes(q) || (it.description || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, filter]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setProgress(0);
    setShow(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      title: it.title || "",
      description: it.description || "",
      category: it.category || "Geral",
      status: it.status || "draft",
      tag: it.tag || "",
      file: null,
    });
    setProgress(0);
    setShow(true);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setForm((p) => ({ ...p, file: null })); return; }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(`Extensão .${ext} não permitida.`);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Arquivo excede ${MAX_MB} MB.`);
      e.target.value = "";
      return;
    }
    setForm((p) => ({ ...p, file: f }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Título é obrigatório."); return; }
    if (!editing && !form.file) { toast.error("Selecione um arquivo."); return; }

    setBusy(true);
    setProgress(0);
    try {
      let saved;
      if (editing) {
        // 1) atualiza metadados
        const { data } = await api.put(`/admin/downloads/${editing.id}`, {
          title: form.title,
          description: form.description,
          category: form.category,
          status: form.status,
          tag: form.tag,
        });
        saved = data;

        // 2) (opcional) substitui arquivo
        if (form.file) {
          const fd = new FormData();
          fd.append("file", form.file);
          const { data: rep } = await api.post(`/admin/downloads/${editing.id}/file`, fd, {
            onUploadProgress: (ev) => {
              if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
            },
          });
          saved = rep;
        }
      } else {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("category", form.category);
        fd.append("status", form.status);
        fd.append("tag", form.tag);
        fd.append("file", form.file);
        const { data } = await api.post("/admin/downloads", fd, {
          onUploadProgress: (ev) => {
            if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
          },
        });
        saved = data;
      }

      toast.success(editing ? "Material atualizado" : "Material criado");
      setShow(false);
      await load();
      void saved;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const setStatusQuick = async (it, status) => {
    try {
      await api.put(`/admin/downloads/${it.id}`, { status });
      toast.success("Status atualizado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/downloads/${id}`);
      toast.success("Material excluído");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto" data-testid="downloads-manager">
      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">Administração</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Gerenciar Materiais</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Faça upload de PDFs, planilhas, slides, imagens e ZIPs (até {MAX_MB} MB). Apenas materiais com status{" "}
            <strong>Publicado</strong> aparecem para os clientes assinantes.
          </p>
        </div>
        <button onClick={openNew} className="btn btn-primary" data-testid="dl-new-button">
          <i className="fa-solid fa-cloud-arrow-up"></i> Novo material
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6 flex flex-wrap items-end gap-3" data-testid="dl-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="overline text-xs text-slate-500">Buscar</label>
          <input
            type="text"
            value={filter.q}
            onChange={(e) => setFilter((p) => ({ ...p, q: e.target.value }))}
            placeholder="Título ou descrição"
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none"
            data-testid="dl-filter-q"
          />
        </div>
        <div>
          <label className="overline text-xs text-slate-500">Categoria</label>
          <select
            value={filter.category}
            onChange={(e) => setFilter((p) => ({ ...p, category: e.target.value }))}
            className="border border-slate-300 px-3 py-2 text-sm bg-white focus:border-emerald-700 focus:outline-none"
            data-testid="dl-filter-category"
          >
            <option value="">Todas</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-xs text-slate-500">Status</label>
          <select
            value={filter.status}
            onChange={(e) => setFilter((p) => ({ ...p, status: e.target.value }))}
            className="border border-slate-300 px-3 py-2 text-sm bg-white focus:border-emerald-700 focus:outline-none"
            data-testid="dl-filter-status"
          >
            <option value="">Todos</option>
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <button
          onClick={() => setFilter({ q: "", category: "", status: "" })}
          className="btn btn-ghost text-xs"
          data-testid="dl-filter-clear"
        >
          Limpar
        </button>
      </div>

      {/* Table */}
      <div className="card">
        {visible.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="dl-empty">
            <i className="fa-solid fa-folder-open text-3xl mb-3 text-slate-300"></i>
            <p className="text-sm">Nenhum material encontrado. Clique em <strong>Novo material</strong> para começar.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 font-semibold uppercase text-xs">Título / Categoria</th>
                <th className="font-semibold uppercase text-xs">Arquivo</th>
                <th className="font-semibold uppercase text-xs">Status</th>
                <th className="font-semibold uppercase text-xs text-right">Downloads</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`dl-row-${it.id}`}>
                  <td className="py-3 pr-4">
                    <div className="font-semibold text-slate-900">{it.title}</div>
                    <div className="text-xs text-slate-500">
                      <span className="text-emerald-700 font-semibold">{it.category}</span>
                      {it.tag && <span className="ml-2 text-amber-700 text-[10px] uppercase font-bold">{it.tag}</span>}
                    </div>
                    {it.description && (
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2 max-w-xl">{it.description}</div>
                    )}
                  </td>
                  <td className="text-xs text-slate-600">
                    <div className="flex items-center gap-2">
                      <i className={`fa-solid ${it.icon} text-lg text-slate-500`}></i>
                      <div>
                        <div className="font-mono uppercase">.{it.ext}</div>
                        <div className="text-[11px] text-slate-500">{it.size_human}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <select
                      value={it.status}
                      onChange={(e) => setStatusQuick(it, e.target.value)}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border bg-white ${STATUS_BADGE[it.status] || ""}`}
                      data-testid={`dl-status-${it.id}`}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="text-right font-mono text-slate-700">{it.downloads_count}</td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/${it.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-ghost text-xs"
                      title="Visualizar arquivo"
                      data-testid={`dl-view-${it.id}`}
                    >
                      <i className="fa-solid fa-eye"></i>
                    </a>
                    <button onClick={() => openEdit(it)} className="btn btn-ghost text-xs" data-testid={`dl-edit-${it.id}`}>
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <ConfirmButton onConfirm={() => del(it.id)} testid={`dl-delete-${it.id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {show && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={() => !busy && setShow(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-white border w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto"
            data-testid="dl-modal"
          >
            <div className="overline mb-2">{editing ? "Editar" : "Novo"} material</div>
            <h2 className="font-display text-2xl font-extrabold mb-6 tracking-tight">
              {editing ? form.title : "Novo material para download"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="overline text-xs text-slate-600">Título *</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none"
                  data-testid="dl-input-title"
                />
              </div>

              <div>
                <label className="overline text-xs text-slate-600">Descrição</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none"
                  data-testid="dl-input-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-xs text-slate-600">Categoria</label>
                  <input
                    type="text"
                    list="dl-category-list"
                    value={form.category}
                    onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                    placeholder="Ex.: Manuais, Comercial…"
                    className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none"
                    data-testid="dl-input-category"
                  />
                  <datalist id="dl-category-list">
                    {categories.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label className="overline text-xs text-slate-600">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full border border-slate-300 px-3 py-2 text-sm bg-white focus:border-emerald-700 focus:outline-none"
                    data-testid="dl-input-status"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="overline text-xs text-slate-600">Tag (opcional)</label>
                <input
                  type="text"
                  value={form.tag}
                  onChange={(e) => setForm((p) => ({ ...p, tag: e.target.value }))}
                  placeholder="Ex.: Novo, Recomendado"
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none"
                  data-testid="dl-input-tag"
                />
              </div>

              <div>
                <label className="overline text-xs text-slate-600">
                  Arquivo {editing ? "(deixe em branco para manter o atual)" : "*"}
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  onChange={handleFile}
                  className="w-full text-sm"
                  data-testid="dl-input-file"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Aceitos: PDF · Word · Excel · PowerPoint · PNG/JPG · ZIP. Máx {MAX_MB} MB.
                  {editing && (
                    <span className="block mt-1">
                      Atual: <span className="font-mono uppercase">.{editing.ext}</span> · {editing.size_human}
                    </span>
                  )}
                </div>
                {progress > 0 && progress < 100 && (
                  <div className="mt-2">
                    <div className="h-1 bg-slate-200 overflow-hidden">
                      <div className="h-full bg-emerald-600 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Enviando {progress}%</div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                type="button"
                onClick={() => setShow(false)}
                disabled={busy}
                className="btn btn-ghost"
                data-testid="dl-cancel"
              >
                Cancelar
              </button>
              <button type="submit" disabled={busy} className="btn btn-primary" data-testid="dl-save">
                {busy
                  ? <><i className="fa-solid fa-spinner fa-spin"></i> Enviando…</>
                  : <><i className="fa-solid fa-check"></i> Salvar</>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
